<template>
    <div>
        <CompassDialog
            ref="importDetailDialog"
            :visible.sync="showDialog"
            size="large"
            class="import-detail-dialog"
            :title="$t.__('settings.import.detail.ImportDetailPopup.import_detail')"
            @closed="$emit('closed')"
        >
            <template #default v-if="!importDetail">
                <CompassLoader class="preloader-container__loader" />
            </template>
            <template #default v-else>
                <div v-if="isImportInErrorOrCancelled" :class="restartImportAlertClasses">
                    <CompassAlert
                        ref="alertImportOnError"
                        :show-close="false"
                        :type="alertType"
                        :description="alertMessage"
                        :action="{
                            label: $t.__(
                                'settings.import.detail.ImportDetailPopup.start_import_again',
                            ),
                            callback: () => onRestartImport(importTypeDetails),
                        }"
                    />
                </div>
                <CompassAlert
                    v-if="isImportV2"
                    type="warning"
                    :description="
                        $t.__('settings.import.detail.ImportDetailPopup.import_carried_out')
                    "
                    :action="{
                        label: $t.__(
                            'settings.import.detail.ImportDetailPopup.see_details_on_old_platform',
                        ),
                        callback: () => goToDetailV2(),
                    }"
                    :show-close="false"
                />

                <div class="card-container">
                    <div class="card card-summary">
                        <h4 class="title">
                            {{ $l10n.__('Résumé') }}
                        </h4>
                        <div>
                            <div class="line-container">
                                <CompassIcon
                                    icon="success-circle-fill"
                                    size="small"
                                    type="success"
                                />
                                <div>
                                    <p class="typographies-paragraph-x-large-bold">
                                        {{
                                            getLabelForLine(
                                                importDetail,
                                                importDetail.processed_rows,
                                            )
                                        }}
                                    </p>
                                    <p class="typographies-paragraph-medium-semibold">
                                        {{
                                            $l10n.n__(
                                                'ligne traitée',
                                                'lignes traitées',
                                                importDetail.processed_rows,
                                                importDetail.processed_rows,
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                            <div class="line-container">
                                <CompassIcon icon="error-circle-fill" size="small" type="alert" />
                                <div>
                                    <p class="typographies-paragraph-x-large-bold">
                                        {{
                                            getLabelForLine(importDetail, importDetail.invalid_rows)
                                        }}
                                    </p>
                                    <p class="typographies-paragraph-medium-semibold">
                                        {{
                                            $l10n.n__(
                                                'ligne en erreur',
                                                'lignes en erreurs',
                                                importDetail.invalid_rows,
                                                importDetail.invalid_rows,
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <template v-if="!isImportInErrorOrCancelled && !isImportV2">
                        <div
                            v-for="subType in getDisplaySubTypeOrder"
                            :key="subType"
                            class="card card-type"
                        >
                            <div class="title">
                                <CompassIcon :icon="getImportIcon(subType)" size="medium" />
                                <h4>
                                    <span
                                        v-for="{ segment, classes } in getImportTitleSegments(
                                            subType,
                                        )"
                                        :class="classes"
                                        >{{ segment }}</span
                                    >
                                </h4>
                            </div>
                            <div>
                                <div class="line-container">
                                    <p class="typographies-paragraph-x-large-bold">
                                        {{
                                            getLabelForLine(
                                                importDetail,
                                                isInRevertStep
                                                    ? getSubTypeResultValues(subType).deleted
                                                    : getSubTypeResultValues(subType).created,
                                            )
                                        }}
                                    </p>
                                    <p
                                        v-if="isInRevertStep"
                                        class="typographies-paragraph-medium-semibold"
                                    >
                                        <span v-if="isSubTypeFemale(subType)">
                                            {{
                                                $l10n.n__(
                                                    'supprimée',
                                                    'supprimées',
                                                    getSubTypeResultValues(subType).deleted,
                                                    getSubTypeResultValues(subType).deleted,
                                                )
                                            }}
                                        </span>
                                        <span v-else>
                                            {{
                                                $l10n.n__(
                                                    'supprimé',
                                                    'supprimés',
                                                    getSubTypeResultValues(subType).deleted,
                                                    getSubTypeResultValues(subType).deleted,
                                                )
                                            }}
                                        </span>
                                    </p>
                                    <p v-else class="typographies-paragraph-medium-semibold">
                                        <span v-if="isSubTypeFemale(subType)">
                                            {{
                                                $l10n.n__(
                                                    'ajoutée',
                                                    'ajoutées',
                                                    getSubTypeResultValues(subType).created,
                                                    getSubTypeResultValues(subType).created,
                                                )
                                            }}
                                        </span>
                                        <span v-else>
                                            {{
                                                $l10n.n__(
                                                    'ajouté',
                                                    'ajoutés',
                                                    getSubTypeResultValues(subType).created,
                                                    getSubTypeResultValues(subType).created,
                                                )
                                            }}
                                        </span>
                                    </p>
                                </div>
                                <div class="line-container">
                                    <p class="typographies-paragraph-x-large-bold">
                                        {{
                                            getLabelForLine(
                                                importDetail,
                                                getSubTypeResultValues(subType).updated,
                                            )
                                        }}
                                    </p>
                                    <p class="typographies-paragraph-medium-semibold">
                                        <span v-if="isSubTypeFemale(subType)">
                                            {{
                                                $l10n.n__(
                                                    'mise à jour',
                                                    'mises à jour',
                                                    getSubTypeResultValues(subType).updated,
                                                    getSubTypeResultValues(subType).updated,
                                                )
                                            }}
                                        </span>
                                        <span v-else>
                                            {{ $l10n.__('mis à jour') }}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
                <div class="tab-container typographies-paragraph-medium-semibold">
                    <div ref="slider" class="slider" />
                    <div
                        ref="tabInformation"
                        class="tab"
                        :class="{ selected: tabSelectedIsInformation }"
                        @click="onTabSelected('tabInformation', tabs.INFORMATIONS)"
                    >
                        <span>
                            {{ $l10n.__('Informations et actions') }}
                        </span>
                    </div>
                    <div
                        ref="tabSettings"
                        class="tab"
                        :class="{ selected: !tabSelectedIsInformation }"
                        @click="onTabSelected('tabSettings', tabs.SETTINGS)"
                    >
                        <span>
                            {{ $l10n.__('Paramètres de l’import') }}
                        </span>
                    </div>
                </div>
                <Transition name="fade" mode="out-in">
                    <component :is="currentTabComponent" :is-import-v2="isImportV2" class="mb-5" />
                </Transition>
            </template>
        </CompassDialog>
    </div>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { importStatus, importProviders } from 'stores/settings/settingsImportsStore.js';
import ImportDetailSettingsTab from 'vcomponents/settings/import/detail/ImportDetailSettingsTab.vue';
import ImportDetailInformationTab from 'vcomponents/settings/import/detail/ImportDetailInformationTab.vue';
import { createNamespacedHelpers } from 'vuex';
import ImportTypeDictionary from 'Dictionaries/imports/ImportTypeDictionary';
const importsStore = createNamespacedHelpers('importsStore');
import importsMixins from 'vcomponents/settings/import/importsMixins.js';
import importService from 'api/import/importService';

export default {
    components: { Preloader, ImportDetailInformationTab, ImportDetailSettingsTab },
    mixins: [importsMixins],
    props: {
        importId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            tabs: {
                INFORMATIONS: 0,
                SETTINGS: 1,
            },
            showDialog: false,
            tabSelected: null,
            shoudlRestart: false,
            importTypeDictionaryExtended: [
                {
                    type: 'address',
                    id: 0,
                    title: window.i18n.__('Adresses'),
                    description: '',
                    icon: 'obj-slsy-address',
                },
            ],
        };
    },
    computed: {
        ...importsStore.mapGetters({
            importDetail: 'getDetailImport',
            importTypeDetails: 'getTypeImport',
            importsList: 'getImportsList',
        }),
        currentTabComponent() {
            return this.tabSelectedIsInformation
                ? ImportDetailInformationTab
                : ImportDetailSettingsTab;
        },
        tabSelectedIsInformation() {
            return this.tabSelected === this.tabs.INFORMATIONS;
        },
        isInRevertStep() {
            return [importStatus.REVERTING, importStatus.REVERTED].includes(
                this.importDetail.status,
            );
        },
        isImportInErrorOrCancelled() {
            return [importStatus.CANCELLED, importStatus.FAILED].includes(this.importDetail.status);
        },
        isImportV2() {
            return this.importDetail.provider === importProviders.IMPORT_V2;
        },
        alertMessage() {
            return this.importDetail.status === importStatus.CANCELLED
                ? this.$l10n.__(
                      'Cet import a été annulé avant son traitement, aucune donnée n’a été impactée.',
                  )
                : this.$l10n.__(
                      'Une erreur nous empêche de traiter votre import, merci de le recommencer.',
                  );
        },
        alertType() {
            return this.importDetail.status === importStatus.CANCELLED ? 'informative' : 'error';
        },
        getDisplaySubTypeOrder() {
            const typeDictionnary = this.importTypeDictionaryExtended.find(
                (item) => item.type === this.importDetail.type,
            );

            return typeDictionnary.displaySubTypeOrder;
        },
        restartImportAlertClasses() {
            let classes = ['restart-import-alert'];

            const statuses = ['queued', 'processing', 'reverting'];

            const predicate = (item) =>
                item.type === this.importDetail.type && statuses.includes(item.status);

            if (this.importsList.findIndex(predicate) >= 0) {
                classes.push('restart-import-alert--disabled');
            }

            return classes.join(' ');
        },
    },
    mounted() {
        this.showDialog = true;
    },
    created() {
        this.importTypeDictionaryExtended = [
            ...this.importTypeDictionaryExtended,
            ...ImportTypeDictionary,
        ];
        this.tabSelected = this.tabs.INFORMATIONS;
        this.fetchImportDetail(this.importId)
            .then(() => {
                this.$refs.slider.style.width = this.$refs.tabInformation.offsetWidth;
            })
            .catch(() => {
                this.showDialog = false;
            });
    },
    beforeDestroy() {
        const url = new URL(location.href);
        if (url.search.length > 0) {
            url.search = '';
            history.replaceState(null, document.title, url.toString());
        }
        if (!this.shoudlRestart) {
            this.resetOptionsImport();
            this.updateTypeImport(null);
        }
        this.resetDetail();
    },
    methods: {
        ...importsStore.mapActions([
            'fetchImportDetail',
            'resetDetail',
            'restartImport',
            'resetOptionsImport',
            'updateTypeImport',
            'updateCustomFields',
            'updateHasCustomFields',
        ]),
        onTabSelected(tabRef, tab) {
            const slider = this.$refs.slider;
            const tabElement = this.$refs[tabRef];
            slider.style.transform = `translateX(${tabElement.offsetLeft}px)`;
            slider.style.width = tabElement.offsetWidth;
            this.tabSelected = tab;
        },
        onRestartImport(importTypeDetails) {
            this.getCustomFields(importTypeDetails)
                .then((customFields) => {
                    this.shoudlRestart = true;
                    // on restart we close this dialog and set the store with history params and flag to start the restart
                    this.showDialog = false;

                    this.updateCustomFields(customFields);
                    this.updateHasCustomFields(customFields.length > 0);

                    this.restartImport(this.importDetail);
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        text: this.$l10n.__(
                            'Une erreur est survenue lors de la récupération des champs personnalisés, merci de réessayer ultérieurement',
                        ),
                        icon: true,
                    });
                });
        },
        getCustomFields(importTypeDetails) {
            const relatedCustomFields = importTypeDetails.customFieldsRelated ?? [];

            if (relatedCustomFields.length === 0) {
                return Promise.resolve(relatedCustomFields);
            }

            const fetchCustomFields = async (relatedCustomField) =>
                await importService
                    .fetchImportCustomFields({
                        related_object: relatedCustomField.category,
                    })
                    .then((customFields) =>
                        customFields.map((field) => {
                            field.relatedCustomField = relatedCustomField;
                            return field;
                        }),
                    )
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            text: this.$l10n.__(
                                'Une erreur est survenue lors de la récupération des champs personnalisés, merci de réessayer ultérieurement',
                            ),
                            icon: true,
                        });

                        return [];
                    });

            return Promise.all(relatedCustomFields.map(fetchCustomFields)).then(
                (groupedCustomFields) => {
                    let result = {};

                    for (const grouped of groupedCustomFields) {
                        for (const customField of grouped) {
                            result[customField.code] = customField;
                        }
                    }

                    return Object.values(result);
                },
            );
        },
        getImportIcon(key) {
            return this.getImportDefinitionByKey(key)?.icon || '';
        },
        getImportTitleSegments(key) {
            const def = this.getImportDefinitionByKey(key);

            if (!def) {
                return [];
            }

            const defaultClasses = 'compass-heading compass-heading--5';

            if (def.titleSegments === undefined) {
                return [
                    {
                        segment: def.title,
                        classes: defaultClasses,
                    },
                ];
            }

            return def.titleSegments.map((segment, idx) => {
                return {
                    segment: `${segment}${idx < def.titleSegments.length - 1 ? ' > ' : ''}`,
                    classes: `${defaultClasses} ${
                        idx < def.titleSegments.length - 1 ? 'compass-heading--secondary' : ''
                    }`,
                };
            });
        },
        getImportDefinitionByKey(key) {
            // API returns in camelCase while in the configuration we have kebab-case
            const kebabCasedKey = _.kebabCase(key);
            return this.importTypeDictionaryExtended.find((item) => item.type === kebabCasedKey);
        },
        goToDetailV2() {
            Utils.relocate(`/?_f=import&importid=${this.importDetail.id}`);
        },
        getSubTypeResultValues(subType) {
            return this.importDetail?.results[subType];
        },
        isSubTypeFemale(subType) {
            return ['address', 'company', 'opportunity'].includes(subType);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.import-detail-dialog {
    z-index: 1001;

    &::v-deep {
        p:not(.bold),
        span {
            margin: 0;
        }
    }

    h4 {
        margin: 0;
        line-height: var(--line-heights-20);
    }

    .card-container {
        display: flex;
        flex-flow: row nowrap;
        gap: var(--spacing-3);
        margin-bottom: var(--spacing-5);

        .card {
            display: flex;
            gap: var(--spacing-2);

            min-width: 15%;
            max-width: 33%;
            padding: var(--spacing-3);

            border-radius: var(--spacing-0);

            .card-divider {
                width: 100%;
                height: 1px;
                margin: var(--spacing-1) 0;
                background-color: $gray-05;
            }

            &.card-summary {
                background-color: $gray-0;
                border: $white solid 1px;

                .line-container {
                    display: flex;
                    gap: var(--spacing-1);

                    &:first-child {
                        margin-bottom: var(--spacing-1);
                        padding-bottom: var(--spacing-1);
                        border-bottom: 1px solid var(--colors-common-border-lighter);
                    }

                    p:last-child {
                        color: var(--colors-common-text-standard-caption);
                    }

                    .compass-icon {
                        margin-top: 3px;
                    }
                }
            }

            &.card-type {
                border: $gray-40 solid 1px;

                .line-container {
                    display: flex;
                    flex-direction: column;

                    &:first-child {
                        margin-bottom: var(--spacing-1);
                        padding-bottom: var(--spacing-1);
                        border-bottom: 1px solid var(--colors-common-border-lighter);
                    }

                    p:last-child {
                        color: var(--colors-common-text-standard-caption);
                    }
                }
            }

            .title {
                display: flex;
                gap: var(--spacing-1);
            }
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 120ms cubic-bezier(0, 0, 0.3, 1);
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .preloader-container__loader::v-deep {
        width: 100%;
        height: 216px;

        .compass-loader--medium {
            width: 53.33px;
            height: 53.33px;

            .compass-icon--medium {
                width: 53.33px;
                height: 53.33px;
            }
        }
    }

    .tab-container {
        position: relative;

        display: flex;
        flex-direction: row;
        gap: var(--spacing-1);

        margin-bottom: var(--spacing-3);

        .tab {
            cursor: pointer;

            position: relative;

            margin-bottom: 6px;
            padding: var(--spacing-0) var(--spacing-1);

            color: var(--colors-component-text-navigation-default);

            border-radius: var(--spacing-0);

            transition: background-color 200ms cubic-bezier(0, 0, 0.3, 1);

            &:hover {
                background-color: var(--colors-component-background-navigation-hover);
            }

            &.selected {
                color: var(--colors-component-text-navigation-active-default);
                background-color: var(--colors-component-background-navigation-active-default);

                &:hover {
                    background-color: var(--colors-component-background-navigation-active-hover);
                }
            }
        }

        .slider {
            position: absolute;
            z-index: 1;
            bottom: 0;
            left: 0;

            display: inline-block;

            height: 2px;

            background-color: var(--colors-component-border-actionable-active-default);

            transition: all 0.2s cubic-bezier(0, 0, 0.3, 1);
        }
    }

    .tab-container::after {
        content: '';

        position: absolute;
        bottom: 0;
        left: 0;

        width: 100%;
        height: 1px;

        background-color: var(--colors-common-border-default);
    }
}
</style>
<style lang="scss">
#compass-tooltip-container {
    .tooltip-disable-btn {
        z-index: 1002;
        white-space: pre-line;
    }
}

.restart-import-alert--disabled {
    .compass-alert__right-container {
        cursor: not-allowed;
    }

    .compass-button {
        pointer-events: none;
    }

    .compass-button--tertiary {
        background-color: transparent;
        color: var(--colors-common-text-standard-disabled, #8c909c);
    }
}
</style>
